/**
 * GameAccordion Component
 * 
 * Displays a collapsible accordion for a game with its achievements.
 * Achievements are loaded on-demand when the accordion is expanded.
 * 
 */

import React, { useState, useEffect } from 'react';
import { api } from '../../../../services/api';
import { Achievement } from '../../../../types/achievements';
import { getImageUrl, parseImageUrl } from '../../../../utils/imageResolution';
import noImage from '../../../../assets/no-image.jpg';
import { AchievementCard } from './AchievementCard';
import styles from './GameAccordion.module.scss';

/**
 * Props for GameAccordion component
 */
export interface GameAccordionProps {
  gameId: string;
  gameName: string;
  totalAchievements: number;
  unlockedCount: number;
  unlockPercentage: number;
  userId: string;
  coverUrl?: string;
}

/**
 * GameAccordion Component
 */
export const GameAccordion: React.FC<GameAccordionProps> = ({
  gameId,
  gameName: initialGameName,
  totalAchievements,
  unlockedCount,
  unlockPercentage,
  userId,
  coverUrl,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gameName, setGameName] = useState<string>(initialGameName);

  /**
   * Load achievements when accordion is expanded
   */
  useEffect(() => {
    if (isExpanded && achievements.length === 0 && !loading) {
      loadAchievements();
    }
  }, [isExpanded]);

  /**
   * Fetch achievements for this game
   */
  const loadAchievements = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.achievements.getUserGameAchievements(userId, gameId);
      
      // Update game name from API response if available
      if (response.gameName) {
        setGameName(response.gameName);
      }
      
      // Filter out hidden achievements that are not unlocked
      const visibleAchievements = response.achievements.filter(
        achievement => !achievement.hidden || achievement.unlocked
      ).sort((a, b) => Number(b.unlocked) - Number(a.unlocked));
      
      setAchievements(visibleAchievements);
    } catch (err: any) {
      console.error(`Failed to load achievements for game ${gameId}:`, err);
      setError('Failed to load achievements');
    } finally {
      setLoading(false);
    }
  };

  // Resolve coverUrl to small tier for the accordion's compact display
  const resolvedCoverUrl = (() => {
    if (!coverUrl) return noImage;
    const parsed = parseImageUrl(coverUrl);
    if (!parsed) return coverUrl;
    return getImageUrl(parsed.imageId, "small") ?? coverUrl;
  })();

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className={styles.accordion}>
      {/* Accordion Header */}
      <button
        type="button"
        className={styles.header}
        onClick={toggleExpanded}
        aria-expanded={isExpanded}
        aria-controls={`game-${gameId}-content`}
      >
        <div className={styles.headerContent}>
          {/* Game Cover */}
          <img
            src={resolvedCoverUrl}
            alt={`${gameName} cover`}
            className={styles.cover}
            loading="lazy"
            onError={(e) => {
              if (e.currentTarget.src !== noImage) {
                e.currentTarget.src = noImage;
              }
            }}
          />

          {/* Game Info */}
          <div className={styles.gameInfo}>
            <h3 className={styles.gameName}>{gameName}</h3>
            <div className={styles.stats}>
              <span className={styles.count}>
                {unlockedCount} / {totalAchievements}
              </span>
              <span className={styles.percentage}>
                {Math.round(unlockPercentage)}%
              </span>
            </div>
          </div>

          {/* Progress Bar */}
          <div className={styles.progressContainer}>
            <div
              className={styles.progressBar}
              role="progressbar"
              aria-valuenow={unlockPercentage}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`${gameName} completion: ${Math.round(unlockPercentage)}%`}
            >
              <div
                className={styles.progressFill}
                style={{ width: `${unlockPercentage}%` }}
              />
            </div>
          </div>
        </div>

        {/* Expand/Collapse Icon */}
        <div className={`${styles.icon} ${isExpanded ? styles.expanded : ''}`}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </button>

      {/* Accordion Content */}
      {isExpanded && (
        <div
          id={`game-${gameId}-content`}
          className={styles.content}
          role="region"
          aria-labelledby={`game-${gameId}-header`}
        >
          {loading && (
            <div className={styles.loading}>
              <div className={styles.spinner} aria-label="Loading achievements..." />
              <p>Loading achievements...</p>
            </div>
          )}

          {error && (
            <div className={styles.error}>
              <p>{error}</p>
              <button
                type="button"
                className={styles.retryButton}
                onClick={loadAchievements}
              >
                Retry
              </button>
            </div>
          )}

          {!loading && !error && achievements.length > 0 && (
            <div className={styles.achievementList}>
              {achievements.map(achievement => (
                <AchievementCard
                  key={achievement.achievementId}
                  achievement={achievement}
                  gameName={gameName}
                />
              ))}
            </div>
          )}

          {!loading && !error && achievements.length === 0 && (
            <div className={styles.empty}>
              <p>No achievements found for this game</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GameAccordion;
